// src/context/NotificationContext.jsx
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { refreshAccessToken } from '../utils/wsTokenRefresh';
import { deleteNotification, getAllNotifications, markAllNotificationAsRead, markNotificationAsRead } from '../services/endpoint';
import axios from '../api/axios';

export const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

const socket_url = import.meta.env.VITE_SOCKET_URL;

export const NotificationProvider = ({ children }) => {
  const [message, setMessage] = useState({ msg: null, type: null });
  const [notifications, setNotifications] = useState({ results: [], next: null, count: 0 });
  const [unread, setUnread] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const socketRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const messageTimerRef = useRef(null);

  // --- WebSocket Token Refresh ---
  const refreshWebSocketToken = useCallback(async () => {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      socketRef.current?.close();
      return;
    }
    // Send new token to WebSocket server
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'refresh_token',
        token: newToken,
      }));
    }
  }, []);

  // --- WebSocket Connection ---
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;
      if (socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING) {
        socketRef.current.close(1000, 'Cleanup');
      }
      socketRef.current = null;
    }
  }, []);

  // Fallback: check force-logout flag on reconnect (covers WS-down window)
  const checkForceLogoutFallback = useCallback(async () => {
    const token = sessionStorage.getItem('access_token');
    const userRole = sessionStorage.getItem('user_role');
    if (!token || userRole === 'admin' || userRole === 'superadmin') return;
    try {
      const response = await axios.get('/api/accounts/check-force-logout/');
      if (response.data?.data?.force_logout) {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = '/login';
      }
    } catch {
      // Ignore — WS will handle it going forward
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = sessionStorage.getItem('access_token');
    const role = sessionStorage.getItem('user_role');
    // Superadmin operates in public schema with no tenant WebSocket support
    if (!token || !socket_url || role === 'superadmin') return;
    if (socketRef.current?.readyState === WebSocket.OPEN) return;
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) return;

    try {
      const wsUrl = `${socket_url}/ws/notifications/?token=${token}`;
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0;
        // On reconnect, check if a force-logout was issued while disconnected
        checkForceLogoutFallback();
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);

          // Token refresh messages from server
          if (payload.type === 'token_refresh_required') {
            refreshWebSocketToken();
            return;
          }
          if (payload.type === 'token_refreshed' || payload.type === 'token_refresh_error') {
            return;
          }

          // Initial unread notifications list (sent on connect)
          if (payload.unread_notifications && Array.isArray(payload.unread_notifications)) {
            const normalized = payload.unread_notifications.map(normalizeNotification);
            setUnread(normalized);
            setUnreadCount(normalized.length);
            return;
          }

          // New real-time notification
          if (payload.type === 'new_notification' && payload.data) {
            const notif = normalizeNotification(payload.data);
            setUnread(prev => {
              // Avoid duplicates
              if (prev.some(n => n.id === notif.id)) return prev;
              return [notif, ...prev];
            });
            setUnreadCount(prev => prev + 1);
            setHasNewNotification(true);

            // Show browser notification if permitted
            if ('Notification' in window && Notification.permission === 'granted') {
              new window.Notification(notif.title, { body: notif.message });
            }
            return;
          }

          // Notification update (e.g. read status changed from another tab)
          if (payload.type === 'notification_update') {
            // Refresh unread list
            return;
          }

          // Heartbeat acknowledgement
          if (payload.type === 'heartbeat_ack') {
            if (payload.status === 'no_session') {
              // Agent's session record is missing — log for visibility but don't disconnect.
              // This can happen if the session expired or wasn't created yet.
              console.warn('[WS] heartbeat_ack: no active agent session found on server');
            }
            return;
          }

          // Force logout
          if (payload.type === 'force_logout') {
            sessionStorage.clear();
            localStorage.clear();
            window.location.href = '/login';
            return;
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      socket.onerror = () => {};

      socket.onclose = async (event) => {
        socketRef.current = null;

        // Auth failure — refresh token before reconnecting
        if (event.code === 4001 || event.code === 4002) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            setTimeout(connectWebSocket, 1000);
            return;
          }
          window.location.href = '/login';
          return;
        }

        // Normal reconnect on abnormal close
        if (event.code !== 1000 && event.code !== 1001 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        }
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, []);

  // Normalize notification object to consistent shape
  const normalizeNotification = (n) => ({
    id: n.id,
    title: n.title || 'Notification',
    message: n.message || '',
    notification_type: n.notification_type || 'info',
    category: n.category || 'general',
    redirect_url: n.redirect_url || '',
    is_read: n.is_read || false,
    created_at: n.created_at || null,
  });

  // Initialize WebSocket
  useEffect(() => {
    if (hasInitializedRef.current) return;

    const checkAndConnect = () => {
      const token = sessionStorage.getItem('access_token');
      if (token && !hasInitializedRef.current) {
        hasInitializedRef.current = true;
        connectWebSocket();
      }
    };

    checkAndConnect();

    const handleAuthChange = () => {
      cleanup();
      hasInitializedRef.current = false;
      reconnectAttemptsRef.current = 0;
      setUnread([]);
      setUnreadCount(0);
      setNotifications({ results: [], next: null, count: 0 });
      setTimeout(() => {
        hasInitializedRef.current = false;
        checkAndConnect();
      }, 500);
    };

    window.addEventListener('authStateChange', handleAuthChange);

    return () => {
      window.removeEventListener('authStateChange', handleAuthChange);
      cleanup();
      hasInitializedRef.current = false;
    };
  }, [connectWebSocket, cleanup]);

  // --- Heartbeat over WebSocket (replaces HTTP polling) ---
  useEffect(() => {
    const userRole = sessionStorage.getItem('user_role');
    const token = sessionStorage.getItem('access_token');

    if (!token || (userRole !== 'agent' && userRole !== 'supervisor')) return;

    const sendHeartbeat = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: 'heartbeat' }));
      }
      // If socket isn't open yet, the next tick will retry — no-op is safe here
    };

    // Delay the first heartbeat to give the WebSocket time to connect,
    // then repeat every 30 s.
    const initialDelay = setTimeout(sendHeartbeat, 2000);
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(heartbeatInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Toast/Banner Notification ---
  const showNotification = (msg, type = 'success') => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setMessage({ msg, type });
    messageTimerRef.current = setTimeout(() => {
      setMessage({ msg: null, type: null });
    }, 3000);
  };

  // --- API Methods ---
  const fetchNotifications = async (page = 1, page_size = 20) => {
    try {
      setLoading(true);
      const response = await getAllNotifications(page, page_size);
      const data = response.data;

      if (page === 1) {
        setNotifications(data);
      } else {
        setNotifications(prev => ({
          ...data,
          results: [
            ...prev.results,
            ...data.results.filter(n => !prev.results.some(p => p.id === n.id))
          ]
        }));
      }
      return data;
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const response = await markNotificationAsRead(id);
      if (response.status === 200) {
        // Update local state immediately (optimistic)
        setUnread(prev => prev.filter(n => n.id !== id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => ({
          ...prev,
          results: prev.results.map(n =>
            n.id === id ? { ...n, is_read: true } : n
          )
        }));
        return true;
      }
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
      return false;
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await markAllNotificationAsRead();
      if (response.status === 200) {
        // Update local state immediately
        setUnread([]);
        setUnreadCount(0);
        setNotifications(prev => ({
          ...prev,
          results: prev.results.map(n => ({ ...n, is_read: true }))
        }));
        return true;
      }
    } catch (e) {
      console.error('Failed to mark all as read:', e);
      return false;
    }
  };

  const DeleteNotification = async (id) => {
    try {
      const response = await deleteNotification(id);
      if (response.status === 200) {
        // Remove from local state immediately
        setNotifications(prev => ({
          ...prev,
          results: prev.results.filter(n => n.id !== id),
          count: Math.max(0, (prev.count || 0) - 1)
        }));
        setUnread(prev => prev.filter(n => n.id !== id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      }
    } catch (e) {
      console.error('Failed to delete notification:', e);
      return false;
    }
  };

  const clearNewNotificationIndicator = () => setHasNewNotification(false);

  // Fetch notifications on mount (once token is available)
  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    const role = sessionStorage.getItem('user_role');
    if (token && role !== 'superadmin') {
      fetchNotifications(1, 20);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{
      showNotification,
      notifications,
      loading,
      setLoading,
      unread,
      unreadCount,
      markAsRead,
      hasNewNotification,
      markAllAsRead,
      DeleteNotification,
      setHasNewNotification,
      fetchNotifications,
      clearNewNotificationIndicator
    }}>
      {children}
      {message.msg && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 rounded shadow-lg text-white z-[9999] ${message.type === 'success' ? 'bg-green-600' : message.type === 'error' ? 'bg-red-600' : message.type === 'warning' ? 'bg-yellow-600' : 'bg-blue-600'}`}>
          {message.msg}
        </div>
      )}
    </NotificationContext.Provider>
  );
};
